import React from 'react';
import { Dialog, DialogContent, DialogTitle, DialogHeader } from '@/components/ui/dialog';
import { CurrencyDollarIcon } from '@heroicons/react/24/solid';

const PaymentSelectionModal = ({ open, setOpen, onPaymentTypeSelect }) => {
  const handlePaymentTypeSelect = (paymentType) => {
    onPaymentTypeSelect(paymentType);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex gap-2">
            <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-paper-inset sm:mx-0 sm:h-10 sm:w-10">
              <CurrencyDollarIcon className="h-6 w-6 text-ink-muted" />
            </div>
            <div>
              <DialogTitle className="text-md leading-6 text-ink-muted">Create Payment</DialogTitle>
              <p className="text-sm leading-6 text-ink-muted">
              Process a payment. Accept donations, sell event tickets, or log offline payments.
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="mt-3 grid grid-cols-2 gap-4">
          {/* Fundraising Payment Option */}
          <div
            onClick={() => handlePaymentTypeSelect('fundraiser')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handlePaymentTypeSelect('fundraiser');
              }
            }}
            role="button"
            tabIndex={0}
            className="group cursor-pointer rounded-lg border-2 border-edge bg-paper-inset p-6 transition-all duration-200 hover:border-ink-muted hover:bg-paper-muted focus:outline-none focus:ring-2 focus:ring-sage"
          >
            <div className="text-center">
              <div className="mx-auto mb-1.5 flex h-16 w-16 items-center justify-center rounded-full">
                <svg
                  width="51"
                  height="50"
                  viewBox="0 0 51 50"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M40.5 36.25C41.5355 36.25 42.375 37.0895 42.375 38.125V40.084C42.375 43.0457 39.5257 45.0973 36.7471 44.3252C26.6068 41.5069 15.9183 40 4.875 40C3.83947 40 3 39.1605 3 38.125C3 37.0895 3.83947 36.25 4.875 36.25C16.2608 36.25 27.2868 37.8036 37.751 40.7119C38.2153 40.841 38.625 40.4881 38.625 40.084V38.125C38.625 37.0895 39.4645 36.25 40.5 36.25ZM45.5 7.5C46.8807 7.5 48 8.61929 48 10V30C48 31.3807 46.8807 32.5 45.5 32.5H5.5C4.11929 32.5 3 31.3807 3 30V10C3 8.61929 4.11929 7.5 5.5 7.5H45.5ZM25.5 12.5C21.3579 12.5 18 15.8579 18 20C18 24.1421 21.3579 27.5 25.5 27.5C29.6421 27.5 33 24.1421 33 20C33 15.8579 29.6421 12.5 25.5 12.5ZM10.5 17.5C9.11929 17.5 8 18.6193 8 20C8 21.3807 9.11929 22.5 10.5 22.5C11.8807 22.5 13 21.3807 13 20C13 18.6193 11.8807 17.5 10.5 17.5ZM40.5 17.5C39.1193 17.5 38 18.6193 38 20C38 21.3807 39.1193 22.5 40.5 22.5C41.8807 22.5 43 21.3807 43 20C43 18.6193 41.8807 17.5 40.5 17.5Z"
                    fill="#3A3429"
                  />
                </svg>
              </div>
              <h3 className="text-sm font-semibold text-ink group-hover:text-ink">
                Fundraising Payment
              </h3>
              <p className="mt-1 text-xs text-ink-muted group-hover:text-ink-secondary">
                Process a payment for a fundraiser
              </p>
            </div>
          </div>

          {/* Event Payment Option */}
          <div
            onClick={() => handlePaymentTypeSelect('event')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handlePaymentTypeSelect('event');
              }
            }}
            role="button"
            tabIndex={0}
            className="group cursor-pointer rounded-lg border-2 border-edge bg-paper-inset p-6 transition-all duration-200 hover:border-ink-muted hover:bg-paper-muted focus:outline-none focus:ring-2 focus:ring-sage"
          >
            <div className="text-center">
              <div className="mx-auto mb-1.5 flex h-16 w-16 items-center justify-center rounded-full">
                <svg
                  width="51"
                  height="50"
                  viewBox="0 0 51 50"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3.625 13.2812C3.625 11.1239 5.37389 9.375 7.53125 9.375H43.4688C45.6261 9.375 47.375 11.1239 47.375 13.2812V19.5861C47.375 20.1439 47.0777 20.6594 46.5949 20.9386C45.1897 21.7515 44.25 23.2669 44.25 25C44.25 26.7331 45.1897 28.2485 46.5949 29.0614C47.0777 29.3406 47.375 29.8561 47.375 30.4139V36.7188C47.375 38.8761 45.6261 40.625 43.4688 40.625H7.53125C5.37389 40.625 3.625 38.8761 3.625 36.7188V30.4139C3.625 29.8561 3.92232 29.3406 4.40512 29.0614C5.81029 28.2485 6.75 26.7331 6.75 25C6.75 23.2669 5.81029 21.7515 4.40512 20.9386C3.92232 20.6594 3.625 20.1439 3.625 19.5861V13.2812ZM34.875 10.9375C35.7379 10.9375 36.4375 11.6371 36.4375 12.5V14.0625C36.4375 14.9254 35.7379 15.625 34.875 15.625C34.0121 15.625 33.3125 14.9254 33.3125 14.0625V12.5C33.3125 11.6371 34.0121 10.9375 34.875 10.9375ZM36.4375 20.3125C36.4375 19.4496 35.7379 18.75 34.875 18.75C34.0121 18.75 33.3125 19.4496 33.3125 20.3125V21.875C33.3125 22.7379 34.0121 23.4375 34.875 23.4375C35.7379 23.4375 36.4375 22.7379 36.4375 21.875V20.3125ZM34.875 26.5625C35.7379 26.5625 36.4375 27.2621 36.4375 28.125V29.6875C36.4375 30.5504 35.7379 31.25 34.875 31.25C34.0121 31.25 33.3125 30.5504 33.3125 29.6875V28.125C33.3125 27.2621 34.0121 26.5625 34.875 26.5625ZM36.4375 35.9375C36.4375 35.0746 35.7379 34.375 34.875 34.375C34.0121 34.375 33.3125 35.0746 33.3125 35.9375V37.5C33.3125 38.3629 34.0121 39.0625 34.875 39.0625C35.7379 39.0625 36.4375 38.3629 36.4375 37.5V35.9375ZM13 25C13 24.1371 13.6996 23.4375 14.5625 23.4375H25.5C26.3629 23.4375 27.0625 24.1371 27.0625 25C27.0625 25.8629 26.3629 26.5625 25.5 26.5625H14.5625C13.6996 26.5625 13 25.8629 13 25ZM14.5625 29.6875C13.6996 29.6875 13 30.3871 13 31.25C13 32.1129 13.6996 32.8125 14.5625 32.8125H20.8125C21.6754 32.8125 22.375 32.1129 22.375 31.25C22.375 30.3871 21.6754 29.6875 20.8125 29.6875H14.5625Z"
                    fill="#3A3429"
                  />
                </svg>
              </div>
              <h3 className="text-sm font-semibold text-ink group-hover:text-ink">
                Event Payment
              </h3>
              <p className="mt-1 text-xs text-ink-muted group-hover:text-ink-secondary">
                Process payment for event tickets
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentSelectionModal;

